import {
  HARDINESS_BODY_SPLIT,
  HARDINESS_SECTION_TITLE,
} from './hardinessCardsData'
import { SKILLS_SECTION_TITLE } from './jobProfileSkillsData'

const PAGE_FEATURES = {
  psychometry: { accordion: true, wideAccordions: true, bigFiveTraits: true },
  personlighetstesten: {
    accordion: true,
    wideAccordions: true,
    bigFiveTraits: true,
    hardinessCards: true,
  },
  Interpret: { accordion: true },
  'tolke resultater': { accordion: true },
  'important aspects': { accordion: true },
  'viktige punkter': { accordion: true },
  'why test?': { wideSummary: true },
  'hvorfor teste': { wideSummary: true },
  evnetesten: { accordion: true, wideAccordions: true },
  'communicate results': { accordion: true, pinImages: true },
  'strukturerte intervjuer': { accordion: true, pinImages: true },
  'job analysis': {
    accordion: true,
    pinImages: true,
    pinFirstImageOnly: true,
    skillCards: true,
  },
  jobbanalyse: {
    accordion: true,
    pinImages: true,
    pinFirstImageOnly: true,
    skillCards: true,
  },
}

const SUMMARY_MARKERS = {
  en: '> ## Summary',
  nb: '> ## Oppsummering',
}

const IMAGE_PATTERN = /!\[[^\]]*\]\([^)]+\)/g

const SIDE_IMAGE_ACCORDION_TITLES = {
  en: ['What is processing speed?'],
  nb: ['Hva er Generelle Mentale Evner?'],
}

const trimBody = body => body.replace(/\n{3,}/g, '\n\n').trim()

const isSection = (title, titles, language) => title === titles[language]

const extractSideImage = body => {
  const images = body.match(IMAGE_PATTERN) || []
  if (!images.length) return { sideImage: null, body }

  const sideImage =
    images.find(image => image.includes('gma-example')) ||
    images[images.length - 1]

  return {
    sideImage,
    body: trimBody(body.replace(sideImage, '')),
  }
}

export const getPageFeatures = label => PAGE_FEATURES[label] || {}

export const PERSONALITY_FACTORS_SECTION_TITLE = {
  en: 'Personality factors and facets',
  nb: 'Personlighetsfaktorer og -fasetter',
}

export const hasPinnedImages = label =>
  Boolean(getPageFeatures(label).pinImages)

const splitByHeadings = content => {
  const headings = [...content.matchAll(/^## (.+)$/gm)]
  if (!headings.length) return [{ kind: 'markdown', content }]

  const sections = []
  const intro = content.slice(0, headings[0].index).trim()
  if (intro) sections.push({ kind: 'markdown', content: intro })

  headings.forEach((match, index) => {
    const bodyStart = match.index + match[0].length
    const bodyEnd =
      index + 1 < headings.length ? headings[index + 1].index : content.length

    sections.push({
      kind: 'accordion',
      title: match[1].trim(),
      content: content.slice(bodyStart, bodyEnd).trim(),
    })
  })

  return sections
}

const extractSummary = (content, language) => {
  const marker = SUMMARY_MARKERS[language]
  if (!marker) return { body: content, summary: null }

  const markerIndex = content.indexOf(marker)
  if (markerIndex === -1) return { body: content, summary: null }

  return {
    body: content.slice(0, markerIndex).trim(),
    summary: content.slice(markerIndex).trim(),
  }
}

const stripImages = content => ({
  images: content.match(IMAGE_PATTERN) || [],
  body: trimBody(content.replace(IMAGE_PATTERN, '')),
})

const prepareAccordionBody = (content, features, pinnedImages) => {
  const body = content.replace(/\n---\s*$/s, '').trim()
  if (!features.pinImages) return body

  const { images, body: withoutImages } = stripImages(body)
  if (!images.length) return body
  if (features.pinFirstImageOnly && pinnedImages.length) return body

  const imagesToPin = features.pinFirstImageOnly ? images.slice(0, 1) : images
  pinnedImages.push(...imagesToPin)

  return features.pinFirstImageOnly
    ? trimBody(body.replace(imagesToPin[0], ''))
    : withoutImages
}

const splitHardinessSection = (accordionBody, language) => {
  const { body } = stripImages(accordionBody)
  const splitIndex = body.indexOf(HARDINESS_BODY_SPLIT[language])
  if (splitIndex === -1) return null

  return {
    bodyBefore: body.slice(0, splitIndex).trim(),
    bodyAfter: body.slice(splitIndex).trim(),
    accordionBody: null,
  }
}

const buildAccordionSection = (section, features, language, pinnedImages) => {
  const { body, summary } = extractSummary(section.content, language)
  let accordionBody = prepareAccordionBody(body, features, pinnedImages)
  let sideImage = null
  let bodyBefore = null
  let bodyAfter = null

  if ((SIDE_IMAGE_ACCORDION_TITLES[language] || []).includes(section.title)) {
    const extracted = extractSideImage(accordionBody)
    sideImage = extracted.sideImage
    if (extracted.sideImage) accordionBody = extracted.body
  }

  if (
    features.hardinessCards &&
    isSection(section.title, HARDINESS_SECTION_TITLE, language)
  ) {
    const split = splitHardinessSection(accordionBody, language)
    if (split) {
      bodyBefore = split.bodyBefore
      bodyAfter = split.bodyAfter
      accordionBody = split.accordionBody
    }
  }

  if (
    features.skillCards &&
    isSection(section.title, SKILLS_SECTION_TITLE, language)
  ) {
    accordionBody = trimBody(accordionBody.replace(IMAGE_PATTERN, ''))
  }

  return {
    kind: 'accordion',
    title: section.title,
    body: accordionBody,
    bodyBefore,
    bodyAfter,
    sideImage,
    summary,
  }
}

export const buildHelpPageSections = (content, language, label) => {
  const features = getPageFeatures(label)

  if (!features.accordion) {
    return {
      features,
      pinnedImages: [],
      sections: [{ kind: 'markdown', content }],
    }
  }

  const pinnedImages = []
  const sections = splitByHeadings(content).map(section =>
    section.kind === 'markdown'
      ? section
      : buildAccordionSection(section, features, language, pinnedImages),
  )

  return { features, pinnedImages, sections }
}
